<template>
  <div class="container section-p70">
    <div class="row mb-2">
      <div class="col">
        <h2 class="title-blue">{{ t("SCHOOLS") }}</h2>
      </div>
    </div>

    <div class="row justify-content-between mb-2 align-items-center">
      <div class="col-auto">
        <div class="d-flex align-items-center">
          <ToggleSwitch v-model="showArchived" @change="resetInstitutionsTable" inputId="id-switch-school-archived"/>
          <label class="ms-2 mb-0" for="id-switch-school-archived">{{ t("ARCHIVED") }}</label>
        </div>
      </div>
      <div class="col-auto">
        <Button :label="t('ADD')" @click="showCreationModal"/>
      </div>
    </div>

    <Tabs v-model:value="activeTabIndex" @update:value="onActiveTabChange">
      <TabList>
        <Tab value="0">{{ t('PARTNER_SCHOOLS') }}</Tab>
        <Tab value="1">{{ t('OTHER_INSTITUTIONS') }}</Tab>
      </TabList>

      <TabPanels>
        <TabPanel value="0">
          <div class="row">
            <div class="col">
              <DataTable
                  :value="schoolItems"
                  striped-rows
                  :removableSort="true"
                  :rowHover="true"
                  :pt="{emptyMessage: { class: 'text-center' }}"
              >
                <template #empty>{{ t('NO_RESULTS') }}</template>
                <Column field="registryCode" :header="t('SCHOOL_CODE')" sortable/>
                <Column field="businessName" :header="t('SCHOOL_NAME')" sortable/>
                <Column v-if="showArchived" field="archived" :header="t('ARCHIVED')" key="archived" sortable>
                  <template #body="{ data }">
                    <Checkbox v-model="data.archived" disabled binary/>
                  </template>
                </Column>
                <Column>
                  <template #body="{ data }">
                    <div class="text-center">
                      <router-link :to="{ name: 'ManageSchool', params: { id: data.id, school: data } }">
                        <Button :label="t('VIEW')"/>
                      </router-link>
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
        </TabPanel>

        <TabPanel value="1">
          <div class="row">
            <div class="col">
              <PaginatedTable id="paginated-institutions"
                              :on-page-change="getInstitutionsData"
                              :fields="institutionFields"
                              ref="institutionTableRef"
              >
                <template v-slot:cell(view)="{item}">
                  <div class="text-center">
                    <router-link :to="{ name: 'ManageInstitution', params: { id: item.id } }">
                      <Button :label="t('VIEW')"/>
                    </router-link>
                  </div>
                </template>

                <template v-slot:cell(isArchived)="{item}">
                  <Checkbox v-model="item.isArchived" disabled binary/>
                </template>
              </PaginatedTable>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>

    <school-creation-modal ref="schoolCreationModalRef" @added-school="getSchoolsData"/>
    <institution-modal ref="institutionCreationModalRef" @after-save="resetInstitutionsTable"/>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {injectAxios} from "@/util/inject";
import {useI18n} from "vue-i18n";
import SchoolCreationModal from "@/views/components/modals/schoolCreationModal.vue";
import InstitutionModal from "@/views/components/modals/InstitutionModal.vue";
import PaginatedTable, {type PaginationRequest} from "@/views/components/PaginatedTable.vue";

const {t} = useI18n();
const axios = injectAxios();
const schools = ref([]);
const showArchived = ref(false);
const schoolCreationModalRef = ref<InstanceType<typeof SchoolCreationModal> | null>(null);
const institutionCreationModalRef = ref<InstanceType<typeof InstitutionModal> | null>(null);
const institutionTableRef = ref<InstanceType<typeof PaginatedTable> | null>(null);
const activeTabIndex = ref("0");

onMounted(async () => {
  await getSchoolsData();
});

const onActiveTabChange = (e: { index: number }) => {
  activeTabIndex.value = e;
};

const isSchoolTabActive = computed(() => {
  return activeTabIndex.value === "0";
});

const resetInstitutionsTable = () => {
  institutionTableRef.value?.reset();
};

const showCreationModal = () => {
  if (isSchoolTabActive.value) {
    schoolCreationModalRef.value?.showNewSchoolCreationModal();
    return;
  }

  institutionCreationModalRef.value?.showCreationModal();
};

const getSchoolsData = async () => {
  const response = await axios.get("/school/schools");
  schools.value = response.data;
};

const getInstitutionsData = async (data: PaginationRequest) => {
  const params = {
    ...data,
    isArchived: showArchived.value ? null : false,
  };

  return await axios.get("/institutions", {params: params});
};

const institutionFields = computed(() => {
  const fields = [
    {key: "registryCode", label: t("REGISTRATION_NUMBER"), sortable: true},
    {key: "businessName", label: t("INSTITUTION_NAME"), sortable: true},
    {key: "view", label: "", sortable: false},
  ];

  if (showArchived.value) {
    const field = {key: "isArchived", label: t("ARCHIVED"), sortable: true};
    fields.splice(-1, 0, field);
  }

  return fields;
});

const schoolItems = computed(() => {
  return showArchived.value
      ? schools.value
      : schools.value.filter((m: any) => !m.archived);
});
</script>
